import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { downloadBlob } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Database, Plus, Search, FileDown, Filter, Building2, FileSpreadsheet, Sparkles, Package,
} from "lucide-react";

const STATUS_BADGES = {
  draft:                     "bg-slate-100 text-slate-700 border-slate-300",
  files_uploaded:            "bg-sky-100 text-sky-800 border-sky-300",
  mapping_required:          "bg-indigo-100 text-indigo-800 border-indigo-300",
  validating:                "bg-sky-100 text-sky-800 border-sky-300",
  validation_failed:         "bg-red-100 text-red-800 border-red-300",
  review_required:           "bg-amber-100 text-amber-800 border-amber-300",
  ready_for_test:            "bg-emerald-100 text-emerald-800 border-emerald-300",
  test_migration:            "bg-teal-100 text-teal-800 border-teal-300",
  awaiting_approval:         "bg-amber-100 text-amber-800 border-amber-300",
  approved:                  "bg-emerald-100 text-emerald-800 border-emerald-300",
  scheduled:                 "bg-sky-100 text-sky-800 border-sky-300",
  production_migration:      "bg-violet-100 text-violet-800 border-violet-300",
  reconciliation_required:   "bg-amber-100 text-amber-800 border-amber-300",
  completed:                 "bg-emerald-600 text-white border-emerald-700",
  rolled_back:               "bg-red-100 text-red-800 border-red-300",
  cancelled:                 "bg-slate-200 text-slate-700 border-slate-300",
};
const STATUS_LABEL = (s) => (s || "").replace(/_/g, " ")
  .replace(/\b\w/g, (m) => m.toUpperCase());

function KpiCard({ label, value, tone = "info", testid }) {
  const tint = {
    info: "border-l-sky-500",
    warn: "border-l-amber-500",
    ok:   "border-l-emerald-500",
    danger: "border-l-red-500",
  }[tone];
  return (
    <div
      className={`bg-white border border-slate-200 border-l-4 ${tint} rounded-md p-4`}
      data-testid={testid}
    >
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
        {label}
      </div>
      <div className="font-heading text-2xl font-semibold text-slate-900 mt-1 tabular-nums">
        {typeof value === "number" ? value.toLocaleString() : value}
      </div>
    </div>
  );
}

const ENTITIES = [
  { key: "service_users", label: "Service Users", group: "People" },
  { key: "staff",         label: "Staff",         group: "People" },
  { key: "medications",   label: "Medication",    group: "Clinical" },
  { key: "care_plans",    label: "Care Plans",    group: "Care"    },
  { key: "risks",         label: "Risk Assessments", group: "Care" },
  { key: "daily_notes",   label: "Daily Notes",   group: "Care" },
  { key: "incidents",     label: "Incidents",     group: "Governance" },
  { key: "training",      label: "Training",      group: "Governance" },
];

function NewMigrationDialog({ open, onClose, onCreated }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    org_name: "", current_system: "", supplier: "", go_live_date: "",
    org_size: { service_users: 0, staff: 0, sites: 1 },
    history_scope: "current", export_formats: ["CSV"],
    entities_selected: ["service_users", "medications", "care_plans"],
    require_separate_approver: false,
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleEntity = (k) => set("entities_selected",
    form.entities_selected.includes(k)
      ? form.entities_selected.filter((x) => x !== k)
      : [...form.entities_selected, k]);
  const toggleFmt = (k) => set("export_formats",
    form.export_formats.includes(k)
      ? form.export_formats.filter((x) => x !== k)
      : [...form.export_formats, k]);

  if (!open) return null;
  const submit = async () => {
    if (!form.org_name.trim()) return toast.error("Organisation name is required");
    try {
      const { data } = await api.post("/migrations", form);
      toast.success(`Migration ${data.ref} created`);
      onCreated?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create migration");
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-start justify-center p-6 overflow-y-auto" data-testid="migration-new-dialog">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl mt-8">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">Start a new migration</div>
            <div className="font-heading text-lg font-semibold text-slate-900">Migration Assessment</div>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700 text-xl" data-testid="migration-new-close">×</button>
        </div>
        <div className="px-6 py-4">
          <div className="flex items-center gap-2 text-[11px] mb-5">
            {["Assessment", "Data", "Formats"].map((label, i) => (
              <React.Fragment key={label}>
                <div className={`px-2 py-1 rounded-full border ${step === i + 1 ? "bg-emerald-600 text-white border-emerald-700" : "bg-slate-100 text-slate-600 border-slate-300"}`}>
                  {i + 1}. {label}
                </div>
                {i < 2 && <div className="w-6 h-px bg-slate-300" />}
              </React.Fragment>
            ))}
          </div>

          {step === 1 && (
            <div className="grid grid-cols-2 gap-4" data-testid="migration-new-step-1">
              <div className="col-span-2">
                <label className="text-[11px] font-semibold text-slate-700 uppercase tracking-widest">Organisation name *</label>
                <Input value={form.org_name} onChange={(e) => set("org_name", e.target.value)} className="mt-1 h-10" data-testid="migration-new-org" />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-slate-700 uppercase tracking-widest">Current care system</label>
                <Input value={form.current_system} onChange={(e) => set("current_system", e.target.value)} className="mt-1 h-10" data-testid="migration-new-system" />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-slate-700 uppercase tracking-widest">Supplier</label>
                <Input value={form.supplier} onChange={(e) => set("supplier", e.target.value)} className="mt-1 h-10" />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-slate-700 uppercase tracking-widest">Expected go-live</label>
                <Input type="date" value={form.go_live_date} onChange={(e) => set("go_live_date", e.target.value)} className="mt-1 h-10" />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-slate-700 uppercase tracking-widest">History scope</label>
                <select value={form.history_scope} onChange={(e) => set("history_scope", e.target.value)} className="mt-1 h-10 w-full border border-slate-300 rounded-md px-2 text-sm">
                  <option value="current">Current only</option>
                  <option value="12m">Last 12 months</option>
                  <option value="3y">Last 3 years</option>
                  <option value="6y">Last 6 years</option>
                  <option value="all">All available history</option>
                </select>
              </div>
              <div className="col-span-2 grid grid-cols-3 gap-3">
                {["service_users", "staff", "sites"].map((k) => (
                  <div key={k}>
                    <label className="text-[11px] font-semibold text-slate-700 uppercase tracking-widest">Est. {k.replace("_", " ")}</label>
                    <Input type="number" min="0" value={form.org_size[k] ?? 0}
                      onChange={(e) => set("org_size", { ...form.org_size, [k]: Number(e.target.value) })}
                      className="mt-1 h-10" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {step === 2 && (
            <div data-testid="migration-new-step-2">
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Select data to migrate</div>
              <div className="grid grid-cols-2 gap-2">
                {ENTITIES.map((e) => (
                  <label key={e.key} className="flex items-center gap-2 border border-slate-200 rounded p-2 hover:bg-slate-50 cursor-pointer" data-testid={`migration-new-entity-${e.key}`}>
                    <input type="checkbox" checked={form.entities_selected.includes(e.key)} onChange={() => toggleEntity(e.key)} />
                    <div>
                      <div className="text-sm font-medium text-slate-800">{e.label}</div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-400">{e.group}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          {step === 3 && (
            <div data-testid="migration-new-step-3">
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Available export formats</div>
              <div className="flex flex-wrap gap-2">
                {["CSV", "XLSX", "JSON", "XML", "PDF", "ZIP", "API", "Database Export", "Other"].map((f) => (
                  <button key={f} type="button"
                    onClick={() => toggleFmt(f)}
                    className={`text-xs px-3 py-1.5 rounded-full border ${form.export_formats.includes(f) ? "bg-emerald-600 text-white border-emerald-700" : "bg-white text-slate-700 border-slate-300"}`}
                    data-testid={`migration-new-fmt-${f}`}
                  >{f}</button>
                ))}
              </div>
              <div className="mt-4 text-[12px] text-slate-500">
                We&apos;ll create your migration project in <b>Draft</b>. You can upload files, map fields and run validation before any live records are created.
              </div>
              <label className="mt-4 flex items-start gap-2 border border-slate-200 rounded p-3 cursor-pointer hover:bg-slate-50" data-testid="migration-new-sod">
                <input type="checkbox" checked={form.require_separate_approver} onChange={(e) => set("require_separate_approver", e.target.checked)} className="mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-slate-800">Require separation of duties (§29)</div>
                  <div className="text-[11px] text-slate-500">The person who prepares this migration cannot also approve it. A different authorised user must sign off before go-live.</div>
                </div>
              </label>
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between">
          <Button variant="outline" disabled={step === 1} onClick={() => setStep(step - 1)} data-testid="migration-new-back">Back</Button>
          {step < 3 ? (
            <Button onClick={() => setStep(step + 1)} className="bg-slate-900 hover:bg-slate-800" data-testid="migration-new-next">Next</Button>
          ) : (
            <Button onClick={submit} className="bg-emerald-600 hover:bg-emerald-700" data-testid="migration-new-submit">Create Migration</Button>
          )}
        </div>
      </div>
    </div>
  );
}

function PreflightDialog({ open, onClose, onConverted }) {
  const [form, setForm] = useState({
    org_name: "", current_system: "",
    entities_planned: ["service_users", "medications", "care_plans"],
    org_size: { service_users: 0, staff: 0, sites: 1 },
    data_notes: "",
  });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [converting, setConverting] = useState(false);
  if (!open) return null;
  const run = async () => {
    if (!form.org_name.trim()) return toast.error("Organisation name is required");
    setBusy(true);
    const t = toast.loading("Running preflight simulation…");
    try {
      const r = await api.post("/migrations/preflight", form);
      setResult(r.data);
      toast.success(`Predicted readiness: ${r.data.predicted_readiness ?? 0}%`, { id: t });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preflight failed", { id: t });
    } finally { setBusy(false); }
  };
  const convert = async () => {
    if (!result) return;
    if (!form.org_name.trim()) return toast.error("Organisation name is required");
    setConverting(true);
    const t = toast.loading("Creating migration draft from preflight…");
    try {
      const body = {
        org_name: form.org_name,
        current_system: form.current_system || null,
        entities_selected: form.entities_planned,
        org_size: form.org_size,
        export_formats: result.suggested_export_formats || ["CSV"],
        history_scope: "current",
      };
      const r = await api.post("/migrations", body);
      const notes = (result.data_to_gather || []).slice(0, 6);
      const risks = (result.clinical_risks || []).slice(0, 4);
      const primer =
        `Preflight predicted readiness: ${result.predicted_readiness ?? 0}% · ` +
        `Time estimate: ${result.time_estimate || "—"}\n\n` +
        (notes.length ? `Data to gather:\n- ${notes.join("\n- ")}\n\n` : "") +
        (risks.length ? `Clinical risks flagged:\n- ${risks.join("\n- ")}` : "");
      try {
        await api.post(`/migrations/${r.data.id}/uat`, { items: {
          preflight_summary: { ok: false, comment: primer },
        } });
      } catch { /* UAT primer is best-effort */ }
      toast.success(`Migration ${r.data.ref} created from preflight`, { id: t });
      onConverted?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create migration", { id: t });
    } finally { setConverting(false); }
  };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleEntity = (k) => set("entities_planned",
    form.entities_planned.includes(k)
      ? form.entities_planned.filter((x) => x !== k)
      : [...form.entities_planned, k]);
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-start justify-center p-6 overflow-y-auto" data-testid="preflight-dialog">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl mt-8">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> AI Preflight Simulation</div>
            <div className="font-heading text-lg font-semibold text-slate-900">Predict blockers before you upload a single file</div>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700 text-xl" data-testid="preflight-close">×</button>
        </div>
        <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-3">
            <div>
              <label className="text-[11px] font-semibold text-slate-700 uppercase tracking-widest">Organisation *</label>
              <input value={form.org_name} onChange={(e) => set("org_name", e.target.value)} className="mt-1 h-9 w-full border border-slate-300 rounded px-2 text-sm" data-testid="preflight-org" />
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-700 uppercase tracking-widest">Current care system</label>
              <input value={form.current_system} onChange={(e) => set("current_system", e.target.value)} className="mt-1 h-9 w-full border border-slate-300 rounded px-2 text-sm" placeholder="e.g. Nourish, Person Centred Software, Excel spreadsheets" />
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-700 uppercase tracking-widest">Entities you plan to migrate</label>
              <div className="grid grid-cols-2 gap-1 mt-1">
                {ENTITIES.map((e) => (
                  <label key={e.key} className="flex items-center gap-1 text-xs border border-slate-200 rounded p-1.5 hover:bg-slate-50">
                    <input type="checkbox" checked={form.entities_planned.includes(e.key)} onChange={() => toggleEntity(e.key)} />
                    {e.label}
                  </label>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {["service_users", "staff", "sites"].map((k) => (
                <div key={k}>
                  <label className="text-[11px] font-semibold text-slate-700 uppercase tracking-widest">{k.replace("_", " ")}</label>
                  <input type="number" min="0" value={form.org_size[k] ?? 0} onChange={(e) => set("org_size", { ...form.org_size, [k]: Number(e.target.value) })} className="mt-1 h-9 w-full border border-slate-300 rounded px-2 text-sm" />
                </div>
              ))}
            </div>
            <div>
              <label className="text-[11px] font-semibold text-slate-700 uppercase tracking-widest">Data notes (freeform)</label>
              <textarea value={form.data_notes} onChange={(e) => set("data_notes", e.target.value)} rows={5} className="mt-1 w-full border border-slate-300 rounded p-2 text-sm" placeholder="e.g. Historical MAR only for last 2 years, PDF assessments only, no NHS numbers captured, some clients have duplicate profiles from a system migration in 2022." data-testid="preflight-notes" />
            </div>
            <Button onClick={run} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="preflight-run">{busy ? "Running…" : "Run AI Preflight"}</Button>
          </div>

          <div className="space-y-3">
            {!result && <div className="text-sm text-slate-500 border border-dashed border-slate-300 rounded p-6 text-center" data-testid="preflight-empty">Fill in the profile on the left and click <b>Run AI Preflight</b>. This never touches production data.</div>}
            {result && (
              <>
                <div className="border border-emerald-200 bg-emerald-50 rounded p-3 flex items-center justify-between" data-testid="preflight-readiness">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">Predicted readiness</div>
                    <div className="font-heading text-3xl font-semibold text-emerald-800">{result.predicted_readiness ?? 0}%</div>
                  </div>
                  <div className="text-right text-[11px] text-emerald-800">
                    <div className="uppercase tracking-widest font-semibold">Estimate</div>
                    <div>{result.time_estimate || "—"}</div>
                  </div>
                </div>
                {(result.likely_blockers || []).length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-red-700 font-semibold mb-1">Likely blockers</div>
                    <div className="space-y-1.5">
                      {result.likely_blockers.map((b, i) => (
                        <div key={i} className="border border-slate-200 rounded p-2" data-testid={`preflight-blocker-${i}`}>
                          <div className="flex items-center justify-between">
                            <div className="text-xs font-medium capitalize">{b.category}</div>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded border ${b.likelihood === "high" ? "bg-red-50 border-red-300 text-red-800" : b.likelihood === "medium" ? "bg-amber-50 border-amber-300 text-amber-800" : "bg-emerald-50 border-emerald-300 text-emerald-800"}`}>{b.likelihood}</span>
                          </div>
                          <div className="text-[12px] text-slate-700 mt-0.5">{b.description}</div>
                          <div className="text-[11px] text-slate-500 mt-0.5"><b>Mitigation:</b> {b.mitigation}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {(result.clinical_risks || []).length > 0 && (
                  <div className="bg-red-50 border-l-4 border-red-500 rounded p-2 text-[12px] text-red-900">
                    <b>Clinical risks flagged.</b>
                    <ul className="list-disc list-inside mt-0.5">{result.clinical_risks.map((r, i) => <li key={i}>{r}</li>)}</ul>
                  </div>
                )}
                {(result.data_to_gather || []).length > 0 && (
                  <div className="text-[12px]">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Data to gather from customer</div>
                    <ul className="list-disc list-inside text-slate-700 mt-0.5">{result.data_to_gather.map((r, i) => <li key={i}>{r}</li>)}</ul>
                  </div>
                )}
                {(result.suggested_export_formats || []).length > 0 && (
                  <div className="text-[11px] text-slate-500">Suggested export formats: <b>{result.suggested_export_formats.join(", ")}</b></div>
                )}
                <div className="pt-2 border-t border-slate-200">
                  <Button onClick={convert} disabled={converting} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="preflight-convert">
                    {converting ? "Creating draft…" : "Convert this preflight into a new migration draft →"}
                  </Button>
                  <div className="text-[10px] text-slate-400 pt-1">Carries the org profile, planned entities and AI notes into the migration wizard. You can still edit anything before uploading files.</div>
                </div>
                <div className="text-[10px] text-slate-400 pt-1">Preflight is purely advisory — a human must still upload, map and validate. No production or staging records are created.</div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function DataMigration() {
  const nav = useNavigate();
  const [dash, setDash] = useState(null);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [openNew, setOpenNew] = useState(false);
  const [openPreflight, setOpenPreflight] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [d, l] = await Promise.all([
        api.get("/migrations/dashboard"),
        api.get("/migrations", { params: { q: q || undefined, status: status || undefined } }),
      ]);
      setDash(d.data);
      setItems(l.data.items || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => items, [items]);

  return (
    <div className="p-6 max-w-[1400px] mx-auto" data-testid="page-data-migration">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-emerald-700 font-semibold">
            <Database className="w-3.5 h-3.5" /> Administration
          </div>
          <h1 className="font-heading text-3xl font-semibold text-slate-900 mt-1">Data Migration Centre</h1>
          <p className="text-sm text-slate-500 mt-1">Safely migrate your existing care records into AYOBELL.</p>
        </div>
        <div className="flex items-center gap-2">
          <a href="/api/migrations/templates/service_users/xlsx" target="_blank" rel="noreferrer"
             className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-slate-300 bg-white text-slate-700 text-sm hover:bg-slate-50"
             data-testid="btn-download-template">
            <FileSpreadsheet className="w-4 h-4" /> Service Users template
          </a>
          <a href="/downloads/LegacyCare_Migration_Pack.zip" download
             className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-amber-300 bg-amber-50 text-amber-900 text-sm hover:bg-amber-100"
             title="Realistic legacy-care export with deliberate errors — upload it via Start New Migration to test the workflow end-to-end."
             data-testid="btn-download-legacy-pack">
            <Package className="w-4 h-4" /> Sample legacy pack (test the workflow)
          </a>
          <Button variant="outline" onClick={() => setOpenPreflight(true)} className="border-sky-300 text-sky-800 hover:bg-sky-50" data-testid="btn-preflight">
            <Sparkles className="w-4 h-4 mr-1.5" /> AI Preflight
          </Button>
          <Button onClick={() => setOpenNew(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-start-migration">
            <Plus className="w-4 h-4 mr-1.5" /> Start New Migration
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="migration-kpis">
        <KpiCard label="Active Migrations" value={dash?.active_migrations ?? 0} tone="info" testid="kpi-active" />
        <KpiCard label="Awaiting Review" value={dash?.awaiting_review ?? 0} tone="warn" testid="kpi-awaiting" />
        <KpiCard label="Validation Issues" value={dash?.validation_issues ?? 0} tone="danger" testid="kpi-issues" />
        <KpiCard label="Completed" value={dash?.completed ?? 0} tone="ok" testid="kpi-completed" />
        <KpiCard label="Records Migrated" value={dash?.records_migrated ?? 0} tone="ok" testid="kpi-records" />
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-md p-3 mb-3 flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
          <input
            value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") load(); }}
            placeholder="Search by ref, organisation, source system…"
            className="w-full h-9 pl-8 pr-3 border border-slate-300 rounded-md text-sm"
            data-testid="migration-search"
          />
        </div>
        <div className="flex items-center gap-1">
          <Filter className="w-4 h-4 text-slate-400" />
          <select value={status} onChange={(e) => { setStatus(e.target.value); }} className="h-9 border border-slate-300 rounded-md px-2 text-sm" data-testid="migration-status-filter">
            <option value="">All statuses</option>
            {Object.keys(STATUS_BADGES).map((s) => <option key={s} value={s}>{STATUS_LABEL(s)}</option>)}
          </select>
          <Button variant="outline" onClick={load} data-testid="migration-refresh">Refresh</Button>
        </div>
      </div>

      {/* List */}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="migration-list">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500">
              <th className="px-3 py-2">Ref</th>
              <th className="px-3 py-2">Organisation</th>
              <th className="px-3 py-2">Source</th>
              <th className="px-3 py-2 text-right">Total</th>
              <th className="px-3 py-2 text-right">Valid</th>
              <th className="px-3 py-2 text-right">Warn</th>
              <th className="px-3 py-2 text-right">Errors</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={9} className="px-3 py-6 text-center text-slate-400">Loading…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={9} className="px-3 py-10 text-center text-slate-400" data-testid="migration-empty">
                  <Building2 className="w-8 h-8 mx-auto text-slate-300" />
                  <div className="mt-2">No migrations yet — start your first migration to move existing records into AYOBELL safely.</div>
                </td>
              </tr>
            )}
            {filtered.map((m) => {
              const s = m.stats || {};
              return (
                <tr key={m.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`migration-row-${m.ref}`}>
                  <td className="px-3 py-2 font-medium text-slate-800">
                    <Link to={`/migration/${m.id}`} className="hover:text-emerald-700 hover:underline">{m.ref}</Link>
                  </td>
                  <td className="px-3 py-2">{m.org_name || "—"}</td>
                  <td className="px-3 py-2">{m.current_system || "—"}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{s.total_records ?? 0}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-emerald-700">{s.valid_records ?? 0}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-amber-700">{s.warnings ?? 0}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-red-700">{s.errors ?? 0}</td>
                  <td className="px-3 py-2">
                    <span className={`text-[10px] uppercase tracking-widest border rounded px-2 py-0.5 font-semibold ${STATUS_BADGES[m.status] || STATUS_BADGES.draft}`}>
                      {STATUS_LABEL(m.status)}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Link to={`/migration/${m.id}`} className="text-emerald-700 hover:text-emerald-800 text-xs font-medium" data-testid={`migration-open-${m.ref}`}>Open →</Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <NewMigrationDialog
        open={openNew}
        onClose={() => setOpenNew(false)}
        onCreated={(m) => { setOpenNew(false); nav(`/migration/${m.id}`); }}
      />
      <PreflightDialog open={openPreflight} onClose={() => setOpenPreflight(false)} onConverted={(m) => { setOpenPreflight(false); nav(`/migration/${m.id}`); }} />
    </div>
  );
}
